import { useMutation } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"

export type PendingEventCleanupItem = {
  event_id: number
  start_datetime: string
  title: string
  partner_id: number | null
  partner_name: string
}

export type PendingEventCleanup = {
  source_email_id: number
  events: PendingEventCleanupItem[]
  suggested_keep_event_id: number | null
}

/** Cleanup-Card: Zwei HERO-Termine wurden als Doppel-Vorschlag angelegt.
 *  User waehlt im Review/E-Mail-Detail welcher behalten wird, andere werden
 *  via DELETE_CALENDAR_EVENT_MUTATION entfernt.
 *
 *  Wird sowohl im Review (auf Kunden-Antwort-Mail) als auch im normalen
 *  E-Mail-Reiter angezeigt — gleicher Backend-Endpoint dahinter.
 *
 *  Modus-Auto: wenn der Plan customer_confirmed_slot=true hat, wird der
 *  Cleanup beim Execute automatisch ausgefuehrt. Dann zeigt die Card nur
 *  einen Vorschau-Hinweis ohne Buttons — markiert den bestaetigten Slot
 *  und welche Events beim Senden geloescht werden.
 */
export function PendingCleanupCard({
  cleanup, onDone, autoConfirmedSlotIso,
}: {
  cleanup: PendingEventCleanup
  onDone: () => void
  /** Wenn gesetzt → Auto-Mode: keine Buttons, nur Vorschau.
   *  ISO-String des vom Kunden bestaetigten Slots (plan.slot.start_datetime).
   *  Events am gleichen Tag wie dieser Slot bleiben markiert, andere werden
   *  als "wird geloescht" angezeigt. */
  autoConfirmedSlotIso?: string | null
}) {
  const cleanupMut = useMutation({
    mutationFn: async (keep_event_id: number | null) => {
      const r = await fetch(`/api/v1/drafts/${cleanup.source_email_id}/cleanup-events`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keep_event_id }),
      })
      if (!r.ok) {
        const txt = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
      return r.json() as Promise<{ ok: boolean; deleted_event_ids: number[]; failed_event_ids: number[] }>
    },
    onSuccess: (d) => {
      const failed = d.failed_event_ids?.length || 0
      if (failed > 0) {
        toast(`Teilweise gelöscht — ${failed} Fehler`, "error")
      } else {
        toast(`✓ ${d.deleted_event_ids.length} Termin(e) gelöscht`)
      }
      onDone()
    },
    onError: (e) => toast("Cleanup fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  const fmtSlot = (iso: string): string => {
    if (!iso) return "(unbekannt)"
    try {
      const d = new Date(iso)
      return d.toLocaleString("de-DE", {
        weekday: "short", day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    } catch { return iso }
  }

  // ── Auto-Mode: Kunde hat einen Slot bestaetigt, beim Senden wird automatisch
  // aufgeraeumt. Card zeigt nur Vorschau, keine Buttons.
  if (autoConfirmedSlotIso) {
    const sameDay = (a: string, b: string) => {
      try {
        return new Date(a).toDateString() === new Date(b).toDateString()
      } catch { return false }
    }
    const kept = cleanup.events.find((ev) => sameDay(ev.start_datetime, autoConfirmedSlotIso))
    const willDelete = cleanup.events.filter((ev) => ev !== kept)
    return (
      <div style={{
        background: "#f0fdf4", border: "0.5px solid #bbf7d0", borderRadius: 8,
        padding: 14, display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>
          ✓ Kunde hat einen Termin bestätigt — Auto-Cleanup beim Senden
        </div>
        {kept && (
          <div style={{
            background: "#fff", padding: "8px 12px", borderRadius: 6,
            fontSize: 12, color: "#166534",
            border: "1px solid #86efac",
          }}>
            <strong>Bestätigt (bleibt):</strong>{" "}
            {fmtSlot(kept.start_datetime)}
            {kept.partner_name && ` · ${kept.partner_name}`}
          </div>
        )}
        {willDelete.length > 0 ? (
          <>
            <div style={{ fontSize: 12, color: "#475569" }}>
              Folgende {willDelete.length === 1 ? "Termin-Vorschlag wird" : "Termin-Vorschläge werden"} beim Senden mit-gelöscht:
            </div>
            {willDelete.map((ev) => (
              <div
                key={ev.event_id}
                style={{
                  background: "#fff", padding: "6px 10px", borderRadius: 6,
                  fontSize: 12, color: "#7c2d12", border: "1px solid #fed7aa",
                  display: "flex", alignItems: "center", gap: 6,
                  textDecoration: "line-through", textDecorationColor: "#dc2626",
                }}
              >
                <span style={{ color: "#dc2626", textDecoration: "none" }}>🗑</span>
                <span>{fmtSlot(ev.start_datetime)}</span>
                {ev.title && (
                  <span style={{ color: "#94a3b8", textDecoration: "none" }}>· {ev.title}</span>
                )}
                {ev.partner_name && (
                  <span style={{ color: "#94a3b8", textDecoration: "none" }}>· {ev.partner_name}</span>
                )}
              </div>
            ))}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Keine weiteren Termine zu löschen.
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{
      background: "#fff7ed", border: "0.5px solid #fed7aa", borderRadius: 8,
      padding: 14, display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#9A3412" }}>
        ⚠ Doppel-Termin offen — Kunde hat geantwortet
      </div>
      <div style={{ fontSize: 12, color: "#7C2D12" }}>
        Du hast zwei Termin-Vorschläge als Blocker im Kalender angelegt. Bitte
        wähle, welcher behalten werden soll — der andere wird in HERO gelöscht.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {cleanup.events.map((ev) => (
          <button
            key={ev.event_id}
            type="button"
            disabled={cleanupMut.isPending}
            onClick={() => {
              if (confirm(`Diesen Termin behalten?\n\n${fmtSlot(ev.start_datetime)}\n${ev.partner_name || "—"}\n\nAlle anderen werden gelöscht.`)) {
                cleanupMut.mutate(ev.event_id)
              }
            }}
            style={{
              background: "#fff", border: "0.5px solid #fdba74",
              padding: "8px 12px", borderRadius: 6, fontSize: 12, color: "#7C2D12",
              cursor: cleanupMut.isPending ? "not-allowed" : "pointer",
              textAlign: "left", display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ fontWeight: 500 }}>✓ Behalten:</span>
            <span>{fmtSlot(ev.start_datetime)}</span>
            {ev.partner_name && <span style={{ color: "#9A3412" }}>· {ev.partner_name}</span>}
          </button>
        ))}
        <button
          type="button"
          disabled={cleanupMut.isPending}
          onClick={() => {
            if (confirm("ALLE Termin-Vorschläge löschen?")) {
              cleanupMut.mutate(null)
            }
          }}
          style={{
            background: "transparent", border: "0.5px solid #D85A30",
            padding: "6px 12px", borderRadius: 6, fontSize: 12, color: "#D85A30",
            cursor: cleanupMut.isPending ? "not-allowed" : "pointer", marginTop: 4,
            alignSelf: "flex-start",
          }}
        >
          {cleanupMut.isPending ? "Lösche…" : "Alle löschen (kein Termin behalten)"}
        </button>
      </div>
    </div>
  )
}
